/**
 * /oauth/authorize?client_id&scope&redirect_uri&state — the consent page of an app's OAuth 2 install (contracts
 * apps.ts, Slack's "<app> is requesting permission"). Signed out: sign in, then back here. Allow installs the app in
 * the picked workspace (I'm an owner or admin there) and goes to the app's redirect URL with a one-time code; Cancel
 * goes there with `error=access_denied`. Never redirects without a click.
 */
import type { OAuthAuthorizeInfoResponse } from "@bundle/contracts";
import { Avatar, Button, Icon, Select, Stack, Surface, Text, scopeLabel, useSession, useT } from "@bundle/ui";
import { useEffect, useState, type ReactNode } from "react";
import type { Container } from "../platform/container.ts";
import { SignInScreen } from "../screens/SignInScreen.tsx";

export type OAuthParams = { clientId: string; scope: string; redirectUri: string; state?: string };

export function parseOAuth(search: string): OAuthParams | undefined {
  const q = new URLSearchParams(search);
  const clientId = q.get("client_id") ?? "";
  const redirectUri = q.get("redirect_uri") ?? "";
  if (clientId === "" || clientId.length > 100 || !/^https?:\/\//.test(redirectUri)) return undefined;
  const p: OAuthParams = { clientId, scope: (q.get("scope") ?? "").slice(0, 500), redirectUri };
  const state = q.get("state");
  if (state !== null && state !== "") p.state = state.slice(0, 1000);
  return p;
}

type Phase = { type: "loading" } | { type: "ready"; info: OAuthAuthorizeInfoResponse } | { type: "invalid" };

export function OAuthPage({ container }: { container: Container }) {
  const t = useT();
  const { state } = useSession();
  const [params] = useState(() => parseOAuth(location.search));
  const [phase, setPhase] = useState<Phase>(params === undefined ? { type: "invalid" } : { type: "loading" });
  const [workspaceId, setWorkspaceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const signedIn = state.status === "signedIn" && state.session !== undefined;

  useEffect(() => {
    const info = container.api.oauthAuthorizeInfo;
    if (!signedIn || params === undefined || info === undefined) return;
    info.call(container.api, params).then(
      (res) => {
        setPhase({ type: "ready", info: res });
        setWorkspaceId(res.workspaces[0]?.id ?? "");
      },
      () => setPhase({ type: "invalid" }),
    );
  }, [signedIn, params, container]);

  if (params !== undefined && !signedIn) {
    return <SignInScreen returnTo={`${location.pathname}${location.search}`} defaultDevEmail={container.mock ? "ada@acme.test" : ""} mock={container.mock} />;
  }

  const shell = (children: ReactNode) => (
    <Stack grow align="center" justify="center" padding="6" background="bg.window" testID="oauth-page">
      <Surface material="elevated" radius="xl" padding="8">
        <Stack gap="5">{children}</Stack>
      </Surface>
    </Stack>
  );

  if (phase.type === "invalid" || params === undefined) {
    return shell(
      <Stack direction="horizontal" align="center" gap="2">
        <Icon name="alert" size="icon.md" color="status.danger" />
        <Text testID="oauth-invalid">{t("oauth.failed")}</Text>
      </Stack>,
    );
  }
  if (phase.type === "loading") return shell(<Text color="text.secondary">{t("oauth.loading")}</Text>);

  const { info } = phase;
  const cancel = () => {
    const url = new URL(params.redirectUri);
    url.searchParams.set("error", "access_denied");
    if (params.state !== undefined) url.searchParams.set("state", params.state);
    window.location.href = url.toString();
  };
  const allow = async () => {
    const approve = container.api.oauthApprove;
    if (approve === undefined || workspaceId === "") return;
    setBusy(true);
    setFailed(false);
    try {
      const req: Parameters<typeof approve>[0] = { clientId: params.clientId, scope: params.scope, redirectUri: params.redirectUri, workspaceId };
      if (params.state !== undefined) req.state = params.state;
      window.location.href = await approve.call(container.api, req);
    } catch {
      setBusy(false);
      setFailed(true);
    }
  };

  return shell(
    <>
      <Stack direction="horizontal" align="center" gap="3">
        <Avatar name={info.app.name} src={info.app.iconUrl} size="avatar.lg" square />
        <Stack gap="0.5">
          <Text variant="title3" weight="bold" testID="oauth-title">
            {t("oauth.title", { app: info.app.name })}
          </Text>
          <Text variant="footnote" color="text.secondary">
            {t("oauth.madeBy", { workspace: info.app.ownerWorkspaceName })}
          </Text>
        </Stack>
      </Stack>
      {info.app.description === undefined ? null : <Text color="text.secondary">{info.app.description}</Text>}
      {info.workspaces.length === 0 ? (
        <Text color="status.danger" testID="oauth-no-workspaces">
          {t("oauth.noWorkspaces")}
        </Text>
      ) : (
        <Select label={t("oauth.workspace")} value={workspaceId} onChange={setWorkspaceId} options={info.workspaces.map((w) => ({ value: w.id, label: w.name }))} testID="oauth-workspace" />
      )}
      <Stack gap="2">
        <Text weight="semibold">{t("oauth.scopesTitle")}</Text>
        {info.scopes.length === 0 ? (
          <Text color="text.secondary">{t("oauth.noScopes")}</Text>
        ) : (
          info.scopes.map((s) => (
            <Stack key={s} direction="horizontal" align="center" gap="2" testID={`oauth-scope-${s.replace(":", "-")}`}>
              <Icon name="check" size="icon.sm" color="status.success" />
              <Text>{scopeLabel(t, s)}</Text>
            </Stack>
          ))
        )}
      </Stack>
      {failed ? (
        <Text variant="footnote" color="status.danger">
          {t("oauth.approveFailed")}
        </Text>
      ) : null}
      <Stack direction="horizontal" gap="2" justify="end">
        <Button variant="ghost" label={t("oauth.cancel")} onPress={cancel} testID="oauth-cancel" />
        <Button variant="primary" label={t("oauth.allow")} loading={busy} disabled={workspaceId === ""} onPress={() => void allow()} testID="oauth-allow" />
      </Stack>
    </>,
  );
}
